/**
 * @module app.cm
 */

import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { ComponentBase } from "app/shared/lib/essette-site";
import { INotesConfig } from 'app/core/models/essette-interfaces';
import { CarePlanService } from 'app/cm/services/care-plan-service';
import { McgInformedService } from 'app/ext/mcg-informed/services/mcg-informed-service';
import { apiCall } from "app/shared/lib/essette-api";
import { deepClone } from 'app/shared/lib/utilities';
import { getReference } from 'app/shared/models/essette-types';
import { Toaster } from 'app/shared/lib/toaster';
import { CasePageContext } from 'app/cm/lib/case-page-context';
import { getCustomFieldDefinitionReferences } from 'app/core/models/essette-types';
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import { replaceCopyrightText } from 'app/ext/mcg-informed/lib/utilities';
import { Redirect } from 'aurelia-router';
import * as CM from 'app/cm/models/essette-api';
import * as Core from 'app/core/models/essette-api';
import * as Mcg from 'app/ext/mcg-informed/interfaces';

@autoinject()
export class EditGoalPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected caseId!: string;
    protected goalId!: string;
    protected readonly: boolean = false;
    protected goal: CM.CarePlanGoal = {};
    protected editGoal: CM.CarePlanGoal = {};
    protected priorities: ReadonlyArray<CM.CarePlanGoalPriority> = [];
    protected resources: ReadonlyArray<Core.Resource> = [];
    protected notesConfig: INotesConfig = {
        serviceRootUrl: 'cm/careplans/goals',
        targetType: 'CM.CarePlanGoal',
        addTargetProperty: this.addGoalProperty.bind(this),
        excludeSystem: false
    };

    protected customFieldTarget: Core.CustomFieldTarget | null = null;

    private navigateBackOnComplete: boolean = true;

    protected mcgCopyright?: string;
    protected mcgCustomContentDisclaimer?: string;
    protected iccCopyrightDisclaimer?: string;

    protected mcgGoal: Mcg.IGoal = {
        interventionsSet: [],
        sanitizedText: null,
        id: '',
        text: '',
        uid: '',
        sequence: null,
        annotationSet: [],
        citationSet: [],
        footnoteSet: [],
        subinterventionsSet: null,
        $expanded: false
    };


    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly context: CasePageContext,
        private readonly service: CarePlanService,
        private readonly mcgInformedService: McgInformedService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
    }

    protected async canActivate({ caseId, goalId }: { caseId: string, goalId: string }): Promise<Redirect | true> {
        if(!(caseId && goalId)) {
            throw new Error('Required parameters [caseId] and/or [goalId] were not found');
        }

        try {
            let caseNumber = this.context.case?.number;
            if(!caseNumber) {
                // load case number for error message if not found on context
                const loadedCase = await this.context.loadCase(caseId, undefined, ['number']);
                caseNumber = loadedCase.number;
            }
            const response = await this.service.loadGoal(goalId, undefined, ['case.id']);
            const goal: CM.CarePlanGoal = response.results[0];
            const cmCase = goal.case;
            if(cmCase?.id !== caseId) {
                // reroute user if task does not belong to the case
                const messageOptions = {
                    what: this.i18n.tr('cm:carePlanGoal.$'),
                    item: this.i18n.tr('cm:case.$').toLowerCase(),
                    number: caseNumber
                };
                const message = this.i18n.tr('ui:messages.errors.doesNotExistFor', messageOptions);
                this.toaster.error().message(message).show();
                return new Redirect('careplan');
            }
            return true;
        } catch(error) {
            throw error;
        }
    }

    protected async activate({ caseId, goalId }: { caseId: string, goalId: string }): Promise<void> {
        if(!(caseId && goalId)) {
            throw new Error('Required parameters [caseId] and/or [goalId] were not found');
        }

        await this.accountContext.load();
        const canEditClosedCase = this.accountContext.hasPermission('CM.Case.Duty.EditClosedCase');

        this.caseId = caseId;
        this.goalId = goalId;
        this.readonly = this.context.caseIsReadOnly(canEditClosedCase);

        this.loadPriorities();

        await this.loadGoal(goalId).then(() => {
            if(this.goal && this.goal.definition && this.goal.definition.id) {
                this.loadGoalResources(this.goal.definition.id);
            }
            if(!this.customFieldTarget && this.context.goalCustomFieldTarget) this.customFieldTarget = deepClone(this.context.goalCustomFieldTarget);
            if(this.goal.custom && !this.goal.custom.target && this.customFieldTarget) {
                this.goal.custom.target = this.customFieldTarget;
            }
            this.editGoal = deepClone(this.goal);
        });

        if(this.editGoal.externalSystem && this.editGoal.externalSystem.code?.toLowerCase() == 'cci') {
            // Set copyright text based on current goal's external assessment version
            const copyright = getApiSetting(this.context.settings, 'mcg.informed.copyright.$display')?.toString();
            const edition = this.editGoal.externalAssessmentVersion ? this.editGoal.externalAssessmentVersion.split('.')[0] : getApiSetting(this.context.settings, 'mcg.informed.edition.$display')?.toString();
            if(copyright) this.mcgCopyright = replaceCopyrightText(copyright, edition);
        }
        if(this.editGoal.externalSystem && this.editGoal.externalSystem.code?.toLowerCase() == 'iccc') {
            const content = this.editGoal.assessmentAttachment?.externalContent?.content;
            const copyright = content ? this.getIccCopyright(content) : undefined;
            if(copyright) this.iccCopyrightDisclaimer = copyright;
        }
    }


    ////****************** Events ***************************************************////


    ////****************** Functions ************************************************////
    private getIccCopyright(content: string): string | undefined {
        if(!content) return undefined;

        const parser = new DOMParser();
        const review: Document = parser.parseFromString(content, 'text/xml');
        const copyright = review.documentElement?.getAttribute('copyright')?.trim();
        return copyright;
    }

    private addGoalProperty(note: CM.CarePlanGoalNote): void {
        note.goal = { id: this.goal.id };
    }

    private constructGoalForSave(): CM.CarePlanGoal | null {
        if(!this.editGoal) { return null; }

        const saveGoal: CM.CarePlanGoal = {
            id: this.editGoal.id,
            priority: getReference(this.editGoal.priority),
            targetDate: this.editGoal.targetDate,
            custom: this.editGoal.custom ? getCustomFieldDefinitionReferences(this.editGoal.custom, this.customFieldTarget) : null
        };

        return saveGoal;
    }

    protected getCardTitle(): string {
        return this.editGoal.externalSystem && this.editGoal.externalSystem.code === 'CCI' ? this.i18n.tr('ext:cci.goal.$long') : `${this.i18n.tr('cm:carePlanGoal.$')}: ${this.editGoal.description}`;
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadGoal(goalId: string): Promise<void> {
        const includes: string[] = [
            'assessmentAttachment.externalContent',
            'custom',
            'custom.target',
            'custom.target.definitions',
            'custom.target.definitions.choices',
            'externalSystem',
            'member',
            'metadata.completed.byUser',
            'metadata.created.byUser',
            'priority',
            'problem',
            'summary'
        ];

        try {
            this.state.beginLoad();
            const response = await this.service.loadGoal(goalId, includes);
            this.goal = response.results[0];

            if(this.goal.externalSystem?.code?.toLowerCase() == 'cci') {
                const response2 = await this.mcgInformedService.loadGoal(this.goal.id!);
                const mcgGoal: Mcg.IGoal = response2.result;
                this.mcgGoal = mcgGoal;

                // Set custom content disclaimer based on parsed content
                if(this.goal.assessmentAttachment?.externalContent?.content) {
                    const content = this.goal.assessmentAttachment.externalContent.content;
                    const assessment: Mcg.Assessment = JSON.parse(content);
                    this.mcgCustomContentDisclaimer = assessment.customGuidelineDisclaimer;
                }
            }
            if(!!this.goal.isCompleted) {
                this.readonly = true;
            }
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadPriorities(): Promise<void> {
        try {
            this.state.beginLoad();
            const response = await this.service.loadCarePlanGoalPriorities();
            this.priorities = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadGoalResources(goalDefinitionId: string): Promise<void> {
        try {
            this.state.beginLoad();
            const response = await this.service.loadResourcesByGoalDefinition(goalDefinitionId);
            this.resources = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async saveGoal(): Promise<void> {
        const saveGoal = this.constructGoalForSave();
        if(!(saveGoal && saveGoal.id)) {
            throw new Error('Unable to properly construct the save goal');
        }

        try {
            this.state.beginSave();
            const response = await this.service.updateCarePlanGoal(saveGoal.id, saveGoal);
            this.toaster.standard.saved('cm:carePlanGoal.$', response.isSuccessful);
            if(response.isSuccessful) {
                this.loadGoal(saveGoal.id);
            }
        } catch(error) {
            this.toaster.standard.saved('cm:carePlanGoal.$', false);
            throw error;
        } finally {
            this.state.endSave();
        }
    }

    @apiCall
    protected async completeGoal(navigateBack: boolean = true): Promise<void> {
        if(!this.goal.id) { throw new Error('Invalid Goal'); }
        this.navigateBackOnComplete = navigateBack;
        if(this.goal.isCompleted) {
            if(this.navigateBackOnComplete) { this.router.navigateToRoute('cm.case.careplan'); }
            return;
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.completeCarePlanGoal(this.goal.id);
            isSuccessful = response.isSuccessful;
            if(this.navigateBackOnComplete) {
                this.router.navigateToRoute('cm.case.careplan');
            } else {
                this.loadGoal(this.goal.id);
            }
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.completed('cm:carePlanGoal.$', isSuccessful);
            this.state.endSave();
        }
    }

}
